import React from 'react';
import classNames from 'classnames';

import { decodeString } from 'lib/stringUtils';

import './styles.themed.scss';

const block = 'pullquote';


/**
 * PullQuote component renders a styled blockquote with optional attribution and quip.
 *
 * @param {object} props - The properties object.
 * @param {string} props.text - The main quote text to display. Required.
 * @param {string|null} [props.attribution=null] - The attribution text for the quote. Optional.
 * @param {string|null} [props.elementId=null] - The unique ID for the component, used for anchoring. Optional.
 * @param {string|null} [props.quip=null] - Additional text or quip to display alongside the attribution. Optional.
 * @param {'small' | 'medium' | 'large'} [props.size='small'] - The size of the quote block, either 'small' or 'large'. Defaults to 'small'.
 * @param {object} [props.dataAttributes] - Additional data attributes to spread onto the root element. Optional.
 * @returns {JSX.Element|null} The rendered PullQuote component or null if no text is provided.
 */
function PullQuote({
  text,
  attribution = null,
  elementId = null,
  quip = null,
  size = 'small',
  dataAttributes,
}) {
  if (!text) {
    return null;
  }

  return (
    <div
      id={elementId && `anchor-${elementId}`}
      className={classNames(
        block,
        {
          [`${block}--small`]: size === 'small',
          [`${block}--large`]: size === 'large',
        },
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(dataAttributes || {})}
      data-testid={`${block}-${size}`}
    >
      <p className={`${block}__quote`} data-testid={`${block}-quote`}>
        {decodeString(text)}
      </p>
      {(attribution || quip) && (
        <p className={`${block}__attribution`}>
          <cite className={`${block}__cite`} data-testid={`${block}-attribution`}>
            {decodeString(attribution)}
          </cite>
          {(attribution && quip) && (
            // add non-breaking space when there's attribution & quip
            <span>&nbsp;</span>
          )}
          <span className={`${block}__quip`} data-testid="pullquote-quip">
            {decodeString(quip)}
          </span>
        </p>
      )}
    </div>
  );
}

export default PullQuote;
